/** The Socket.IO namespace the proxy's frontends connect to (see ``namespace.py``). */
export const DEFAULT_WATCH_NAMESPACE = '/hmi';

/**
 * Rooms proxied 1:1 from ``hmi/local_backend/proxy/topics.py::LOCAL_INBOUND_TEMPLATES``.
 *
 * Deliberately excludes:
 * - ``gensets`` - has no equivalent topic in the new table yet (tracked separately).
 * - ``components/+`` (other than the component rooms listed below) - a wildcard template, not a single joinable topic.
 * - ``interesting`` - not modeled yet (tracked separately).
 *
 * ``metadata`` is special: unlike every other room it is not scoped per unit on the local
 * broker (bare topic ``metadata``, no asset prefix/unit id) - see ``WatchTopicScheme.globalRoomTopics``.
 */
export const WATCH_ROOMS = [
  'state',
  'timeseries',
  'events',
  'feedback',
  'assets/state',
  'assets/timeseries',
  'components/input_data',
  'components/container_data',
  'components/rtu_data',
  'components/plc_data',
  'components/bms_data',
  'config/announcements',
  'metadata',
] as const;

export type WatchRoom = (typeof WATCH_ROOMS)[number];

/**
 * How topics are addressed for one deployment. A room's topic is, by default,
 * ``{assetPrefix}/{unitId}/{room}`` - ``roomSuffixes``/``globalRoomTopics``/``configCommandBase``
 * exist only because the cloud proxy breaks that pattern for some rooms (see MQTT topics
 * reference: cloud renames several data-room suffixes entirely, e.g. ``state`` ->
 * ``asset_data/state``, and nests config commands one level deeper under ``commands/``).
 */
export interface WatchTopicScheme {
  /** The leading segment every non-global room/command topic is rooted under. */
  assetPrefix: string;
  /** Per-room suffix override; a room not listed here uses its own name as the suffix. */
  roomSuffixes?: Partial<Record<WatchRoom, string>>;
  /**
   * Rooms that bypass ``assetPrefix``/``unitId`` scoping entirely - the topic IS this literal
   * string. Local's ``metadata`` is the only current case (bare ``'metadata'``, no prefix/unit).
   */
  globalRoomTopics?: Partial<Record<WatchRoom, string>>;
  /** Suffix ``change_config``/``change_ephemeral``/``request_config`` are nested under. Defaults to ``'config'``. */
  configCommandBase?: string;
}

/** hmi/local_backend/proxy: asset-type prefix (e.g. 'battery'), bare/global ``metadata`` topic, no other overrides. */
export const LOCAL_WATCH_TOPIC_SCHEME = {
  assetPrefix: 'battery',
  globalRoomTopics: { metadata: 'metadata' },
} satisfies WatchTopicScheme;

/**
 * The cloud proxy's expected shape (see the MQTT topics reference): fixed ``'devices'`` prefix,
 * data rooms renamed under ``asset_data``/``external``, config commands nested under
 * ``commands/config``, and ``metadata`` scoped like every other room (not global).
 */
export const CLOUD_WATCH_TOPIC_SCHEME = {
  assetPrefix: 'devices',
  roomSuffixes: {
    state: 'asset_data/state',
    timeseries: 'asset_data/ts',
    'assets/state': 'external/state',
    'assets/timeseries': 'external/ts',
  },
  configCommandBase: 'commands/config',
} satisfies WatchTopicScheme;

export const DEFAULT_WATCH_TOPIC_SCHEME: WatchTopicScheme = LOCAL_WATCH_TOPIC_SCHEME;

/** Rooms that carry replies to requests instead of unsolicited broadcasts. */
export const WATCH_REPLY_ROOMS = ['feedback'] as const;

export const ALFEN_SYSTEM_STATUSES = {
  OFF: 0,
  ON: 1,
  BUSY: 2,
  WAITING_FOR_RESET: 3,
  SUSPENDED: 4,
  RESET_BUSY: 5,
  STARTING_UP: 6,
} as const;

export type AlfenSystemStatus = (typeof ALFEN_SYSTEM_STATUSES)[keyof typeof ALFEN_SYSTEM_STATUSES];

// Shared with hmi/front-end/src/common/constants.ts's old ALFEN_SYSTEM_MODE - single source for both apps.
export const ALFEN_SYSTEM_MODES = {
  STANDBY: 0,
  PQ: 1,
  PF: 2,
  QU: 3,
  PF_QU_PQ: 4,
  PEAK_SHAVING: 5,
  MICROGRID: 6,
} as const;

export type AlfenSystemMode = (typeof ALFEN_SYSTEM_MODES)[keyof typeof ALFEN_SYSTEM_MODES];

export const CONTACTOR_STATUSES = {
  UNKNOWN: -1,
  CLOSED: 0,
  OPEN: 1,
  INTERMEDIATE: 2,
  ALARM: 3,
  ABORTED: 4,
  NOT_PRESENT: 5,
} as const;

export type BatteryContactorStatus = (typeof CONTACTOR_STATUSES)[keyof typeof CONTACTOR_STATUSES];

export const ASSET_BROKEN_STATES = {
  UNKNOWN: -1,
  NOT_BROKEN: 0,
  CONNECTION_IMPAIRED: 1,
  BROKEN: 2,
} as const;

export type AssetBrokenState = (typeof ASSET_BROKEN_STATES)[keyof typeof ASSET_BROKEN_STATES];

export const ASSET_OPERATIONAL_STATES = {
  UNKNOWN: -1,
  IDLE: 0,
  WARMING_UP: 1,
  RAMPING_UP: 2,
  RUNNING: 3,
  RAMPING_DOWN: 4,
} as const;

export type AssetOperationalState = (typeof ASSET_OPERATIONAL_STATES)[keyof typeof ASSET_OPERATIONAL_STATES];

export const GENSET_DESIRED_STATES = {
  OFF: 0,
  ON: 1,
  AUTO: 2,
} as const;

export type GensetDesiredState = (typeof GENSET_DESIRED_STATES)[keyof typeof GENSET_DESIRED_STATES];

export const CURRENT_PROGRAM = {
  NO_PROGRAM: 'NO_PROGRAM',
  GRID_PEAKSHAVE: 'GRID_PEAKSHAVE', //peakshave
  GEN_SET_PEAKSHAVE: 'GEN_SET_PEAKSHAVE', //cycle
  GEN_SET_CYCLE: 'GEN_SET_CYCLE', //cycle
  GEN_SET_NIGHT_MODE: 'GEN_SET_NIGHT_MODE', //cycle or loadshare
  LOADSHARE: 'LOADSHARE', //loadshare
  SHUTDOWN: 'SHUTDOWN', //shutdown
  STANDALONE: 'STANDALONE', //standalone -> load on output
  DROOP_SINGLE: 'DROOP_SINGLE', //droop -> 99% load on input
  ADD_TO_DROOP_CLUSTER: 'ADD_TO_DROOP_CLUSTER', //droop transitional
  REMOTE_STANDBY: 'REMOTE_STANDBY', //standby
  GRID_BOOST: 'GRID_BOOST', //afe
  CLUSTER_DROOP_DISCHARGE: 'CLUSTER_DROOP_DISCHARGE', //droop
  CLUSTER_AFE_CHARGE: 'CLUSTER_AFE_CHARGE', //afe
  CLUSTER_DROOP_PASSIVE_DISCHARGE: 'CLUSTER_DROOP_PASSIVE_DISCHARGE', //droop
  CLUSTER_STANDBY: 'CLUSTER_STANDBY', //afe or droop
  CLUSTER_TRANSPORT_DEPARTURE: 'CLUSTER_TRANSPORT_DEPARTURE', //trailer cluster (afe/droop)
  CLUSTER_TRANSPORT_ARRIVAL: 'CLUSTER_TRANSPORT_ARRIVAL', //trailer cluster (afe/droop)
  AFE_CLUSTER: 'AFE_CLUSTER', // afe
  STANDBY: 'STANDBY', //standby
  CALIBRATE_SOC: 'CALIBRATE_SOC',
  CHARGE_ON_GENSET: 'CHARGE_ON_GENSET', // cycle or loadshare
  CHARGE_ON_GRID: 'CHARGE_ON_GRID', // cycle or loadshare
  CHARGE_ON_GRID_VS_AFE: 'CHARGE_ON_GRID_VS_AFE', // afe cluster
} as const;

export type CurrentProgramId = (typeof CURRENT_PROGRAM)[keyof typeof CURRENT_PROGRAM];

// Names must match the MQTT command topic segment exactly (see #1593 comments) - the proxy
// forwards these verbatim.
export const WATCH_COMMANDS = [
  'turn_batteries_on',
  'turn_batteries_off',
  'set_system_mode',
  'start_esi',
  'stop_esi',
  'send_connect_request',
  'send_disconnect_request',
  'turn_off_24v_batteries',
  'perform_system_reset',
  'set_max_input_current',
  'set_constant_power_setpoint',
  'start_asset',
  'stop_asset',
  'start_operator_program',
  'stop_operator_program',
  'publish_state_data',
] as const;

export type WatchCommand = (typeof WATCH_COMMANDS)[number];

export const WATCH_CONFIG_TABLE_NAMES = [
  'WatchConfig',
  'BatteryConfig',
  'EnergyMeterConfig',
  'GensetConfig',
  'ExternalGensetConfig',
  'ExternalSolarInverterConfig',
  'ExternalEnergyMeterConfig',
  'ExternalBatteryConfig',
  'RelayConfig',
  'RelayGensetConfig',
  'LoadBankConfig',
  'ModbusConfig',
  'MqttConfig',
] as const;

export type WatchConfigTableName = (typeof WATCH_CONFIG_TABLE_NAMES)[number];

export const WATCH_CONFIG_TABLE_NAME = Object.fromEntries(WATCH_CONFIG_TABLE_NAMES.map((name) => [name, name])) as {
  [K in WatchConfigTableName]: K;
};

/** Canonical display order for the HMI/Command Center config tabs. */
export const WATCH_CONFIG_TABLE_ORDER: WatchConfigTableName[] = [
  'WatchConfig',
  'BatteryConfig',
  'ExternalEnergyMeterConfig',
  'ExternalGensetConfig',
  'LoadBankConfig',
  'RelayConfig',
];

/** Sorts a list of Watch config table names into the canonical tab display order.
 *  Unknown names are placed at the end, preserving their relative order. */
export const sortWatchConfigTableNames = <T extends string>(names: T[]): T[] => {
  const orderIndex = new Map<string, number>(WATCH_CONFIG_TABLE_ORDER.map((name, index) => [name, index]));

  return [...names].sort((a, b) => {
    const indexA = orderIndex.get(a) ?? Number.MAX_SAFE_INTEGER;
    const indexB = orderIndex.get(b) ?? Number.MAX_SAFE_INTEGER;
    return indexA - indexB;
  });
};

export const WATCH_ROW_BASED_CONFIG_TABLES = [
  'WatchConfig',
  'BatteryConfig',
  'EnergyMeterConfig',
  'GensetConfig',
] as const;

export const ROW_BASED_CONFIG = 'ROW_BASED_CONFIG';
export const COLUMN_BASED_CONFIG = 'COLUMN_BASED_CONFIG';

export type WatchConfigTableType = typeof ROW_BASED_CONFIG | typeof COLUMN_BASED_CONFIG;

export const DEFAULT_WATCH_REQUEST_TIMEOUT_MS = 10000;
